import React, { useState } from 'react'
import { Button, Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import {
  REPLACEMENT_PRODUCTS,
  getPairKey,
  getProductNombre,
  isPairMatched,
} from './reemplazantesConstants'
import {
  AddMaterialModal,
  ComparisonOverlay,
  MatchesSummary,
  ProductCardsGrid,
} from './reemplazantesComponents'

const { Dragger } = Upload

const BuscarPorImagenTab = () => {
  const [uploadedImage, setUploadedImage] = useState(null)
  const [hasImageResults, setHasImageResults] = useState(false)
  const [selectedCardIds, setSelectedCardIds] = useState([])
  const [comparisonOpen, setComparisonOpen] = useState(false)
  const [matchedPairs, setMatchedPairs] = useState([])
  const [customProducts, setCustomProducts] = useState([])
  const [addMaterialModalOpen, setAddMaterialModalOpen] = useState(false)

  const allProducts = [...REPLACEMENT_PRODUCTS, ...customProducts]

  const selectedProducts = selectedCardIds
    .map((id) => allProducts.find((product) => product.id === id))
    .filter(Boolean)

  const comparisonPair =
    selectedProducts.length === 2 ? selectedProducts : null

  const pairIsMatched =
    comparisonPair &&
    isPairMatched(
      comparisonPair[0].id,
      comparisonPair[1].id,
      matchedPairs,
    )

  const matchedPairLabels = matchedPairs.map(([idA, idB]) => {
    const nombreA = getProductNombre(idA, allProducts)
    const nombreB = getProductNombre(idB, allProducts)
    return {
      key: getPairKey(idA, idB),
      label: `${nombreA} ↔ ${nombreB}`,
    }
  })

  const matchesSummaryTitle =
    matchedPairs.length === 1
      ? '1 par con match'
      : `${matchedPairs.length} pares con match`

  const handleImageUpload = (file) => {
    const isImage = file.type.startsWith('image/')

    if (!isImage) {
      message.error('Solo se permiten archivos de imagen')
      return Upload.LIST_IGNORE
    }

    if (uploadedImage?.preview) {
      URL.revokeObjectURL(uploadedImage.preview)
    }

    setUploadedImage({
      name: file.name,
      preview: URL.createObjectURL(file),
    })
    setHasImageResults(true)
    setSelectedCardIds([])
    setComparisonOpen(false)
    return false
  }

  const handleRemoveImage = () => {
    if (uploadedImage?.preview) {
      URL.revokeObjectURL(uploadedImage.preview)
    }

    setUploadedImage(null)
    setHasImageResults(false)
    setSelectedCardIds([])
    setComparisonOpen(false)
    setMatchedPairs([])
    setCustomProducts([])
  }

  const handleCardClick = (product) => {
    setComparisonOpen(false)
    setSelectedCardIds((prev) => {
      if (prev.includes(product.id)) {
        return prev.filter((id) => id !== product.id)
      }

      if (prev.length < 2) {
        return [...prev, product.id]
      }

      return [prev[1], product.id]
    })
  }

  const handleComparar = () => {
    if (selectedCardIds.length === 2) {
      setComparisonOpen(true)
    }
  }

  const handleMatchToggle = () => {
    if (!comparisonPair) return

    const [idA, idB] = comparisonPair.map((product) => product.id)
    const pairKey = getPairKey(idA, idB)

    setMatchedPairs((prev) => {
      const exists = prev.some(
        (pair) => getPairKey(pair[0], pair[1]) === pairKey,
      )

      if (exists) {
        return prev.filter((pair) => getPairKey(pair[0], pair[1]) !== pairKey)
      }

      return [...prev, [idA, idB]]
    })
    setComparisonOpen(false)
    setSelectedCardIds([])
  }

  const handleDeleteCustomProduct = (productId) => {
    const inMatch = matchedPairs.some((pair) => pair.includes(productId))
    if (inMatch) return

    setCustomProducts((prev) =>
      prev.filter((product) => product.id !== productId),
    )
    setSelectedCardIds((prev) => prev.filter((id) => id !== productId))
  }

  return (
    <div className="reemplazantes-image-tab">
      {!hasImageResults ? (
        <Dragger
          className="reemplazantes-image-dragger"
          accept="image/*"
          showUploadList={false}
          multiple={false}
          beforeUpload={handleImageUpload}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Arrastra una imagen aquí o haz clic para seleccionar
          </p>
          <p className="ant-upload-hint">
            Formatos de imagen compatibles (JPG, PNG, WEBP, etc.)
          </p>
        </Dragger>
      ) : (
        <div className="reemplazantes-image-results">
          <div className="reemplazantes-image-preview-bar">
            {uploadedImage?.preview && (
              <img
                src={uploadedImage.preview}
                alt={uploadedImage.name}
                className="reemplazantes-image-preview-thumb"
              />
            )}
            <div className="reemplazantes-image-preview-info">
              <p className="reemplazantes-image-preview-name">
                {uploadedImage?.name}
              </p>
              <p className="reemplazantes-image-preview-hint">
                Selecciona 2 materiales y presiona Comparar
              </p>
            </div>
            <Button type="link" onClick={handleRemoveImage}>
              Cambiar imagen
            </Button>
          </div>

          <div className="reemplazantes-cards-panel reemplazantes-cards-panel--full">
            <div className="reemplazantes-cards-toolbar">
              <span className="reemplazantes-selection-count">
                {selectedCardIds.length}/2 seleccionados
              </span>
              <div className="reemplazantes-cards-toolbar-actions">
                <Button
                  type="default"
                  onClick={() => setAddMaterialModalOpen(true)}
                >
                  Agregar material
                </Button>
                <Button
                  type="primary"
                  disabled={selectedCardIds.length !== 2}
                  onClick={handleComparar}
                >
                  Comparar
                </Button>
              </div>
            </div>

            <MatchesSummary
              title={matchesSummaryTitle}
              pairs={matchedPairLabels}
            />

            <ProductCardsGrid
              products={allProducts}
              selectedIds={selectedCardIds}
              matchedPairs={matchedPairs}
              onCardClick={handleCardClick}
              onDeleteCustom={handleDeleteCustomProduct}
              dimmed={comparisonOpen}
              highlightMatched={false}
            />

            <AddMaterialModal
              open={addMaterialModalOpen}
              customProducts={customProducts}
              onClose={() => setAddMaterialModalOpen(false)}
              onConfirm={(product) => {
                setCustomProducts((prev) => [...prev, product])
              }}
            />

            {comparisonOpen && comparisonPair && (
              <ComparisonOverlay
                leftProduct={comparisonPair[0]}
                rightProduct={comparisonPair[1]}
                leftTitle="Material 1"
                rightTitle="Material 2"
                leftBadge="Material 1"
                rightBadge="Material 2"
                isMatched={pairIsMatched}
                matchLabel="Match"
                unmatchLabel="Unmatch"
                onClose={() => setComparisonOpen(false)}
                onMatchToggle={handleMatchToggle}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default BuscarPorImagenTab
